import React, {
  useEffect,
  useState,
  useCallback,
  Suspense,
  useRef,
} from 'react';
import {
  DownloadOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  EditOutlined,
  MoreOutlined,
  UploadOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import 'antd/dist/antd.css';
import {
  Button,
  Col,
  ConfigProvider,
  Form,
  Modal,
  notification,
  Row,
  Table,
  Tag,
  Tooltip,
  Dropdown,
} from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import {
  AUTH_ROLE_CAN_APPROVE,
  debouncedDataLoadWaitMs,
  ECOM_STATUS,
  ORDER_TYPES,
  STATUS,
} from 'helpers/constants';
import trTR from 'antd/es/locale/tr_TR';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS, PARENT_ROUTES } from 'helpers/endpoints';
import { deleteSlotTemplates } from 'store/requests/slot';
import { getStoreList } from 'store/requests/definitions';
import ImportStoreInfo from 'components/ImportExcel/ImportStoreInfo';
import { useSelector } from 'react-redux';
import { setSelectedRegionAndStoreFromStores } from 'store/requests/zone';
import { getColorByStatus } from '../../../../helpers/utils';
import { CSVLink } from 'react-csv';
const StoresTableFilters = React.lazy(() => import('./StoresTableFilters'));
let lastQuery = false;
const Edit = React.lazy(() => import('./Edit'));
const StoresView = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { confirm } = Modal;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ clickAndCollect: true, open: true });
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [initialRequest, setInitialRequest] = useState(true);
  const [selectedStore, setSelectedStore] = useState(null);
  const [exportButtonLoading, setExportButtonLoading] = useState(false);
  const csvLink = useRef();
  const [downloadedData, setDownloadedData] = useState([]);
  const [changeStatusButtonDisabled, setChangeStatusButtonDisabled] =
    useState(true);
  const [selectedRows, setSelectedRows] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(null);
  const activeRegionID = useSelector(
    (state) => state.auth?.user?.activeRegion?.id,
  );
  const activeStoreID = useSelector(
    (state) => state.auth?.user?.activeStore?.id,
  );
  const userRegions = useSelector((state) => state?.definitions?.userRegions);
  const userRoles = useSelector((state) => state.auth.userIntrospect.roles);
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [20, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 20,
    },
  });

  const rowSelection = {
    selectedRowKeys: selectedRows?.map((row) => row.code),
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRows(selectedRows);
    },
  };

  const checkAllActive = (array) => {
    if (array?.length === 0) {
      return false;
    }

    const firstActive = array[0]?.open;

    for (let i = 1; i < array?.length; i++) {
      if (array[i]?.open !== firstActive) {
        return false;
      }
    }

    return true;
  };

  useEffect(() => {
    setChangeStatusButtonDisabled(
      !selectedRows ||
        selectedRows.length === 0 ||
        !checkAllActive(selectedRows),
    );
  }, [selectedRows]);

  const fetchCSVData = async () => {
    setExportButtonLoading(true);
    try {
      const queryString = getCSVExportQueryString();
      const storesRes = await getStoreList(queryString);
      const storeData = storesRes.content;

      if (storeData && storeData.length > 0) {
        const getMostRecentStoreDailyStatus = (statuses) => {
          if (!statuses || statuses.length === 0) {
            return '-';
          }

          const mostRecentStatus = statuses.sort((a, b) => b.date - a.date)[0];

          return t(mostRecentStatus.status) || '-';
        };

        const csvData = [
          [
            ...columns.slice(0, -2).map((item) => t(item.title)),
            t('mainZoneMinimumCartAmount'),
            t('storeZoneDeliveryFees'),
            t('courierStartEndTime'),
          ],
          ...storeData?.map((item) => [
            item.code,
            item.name,
            item.latitude,
            item.longitude,
            item.city?.name,
            item.district?.name,
            item.phoneNumber,
            item.storeType,
            item.open ? 'Açık' : 'Kapalı',
            item.clickAndCollect ? 'Evet' : 'Hayır',
            t(item.ecomStatus),
            getMostRecentStoreDailyStatus(item.storeDailyStatuses),
            item.paymentTypeStores
              ?.map((paymentType) => paymentType?.paymentType?.name)
              .join(', '),
            item.availablePaymentOnDelivery === null
              ? '-'
              : item.availablePaymentOnDelivery
                ? t('yes')
                : t('no'),
            t(item.collectionType),
            t(item.alternativeProduct),
            item.areaM2 !== null && item.areaM2 !== undefined ? item.areaM2 : '-',
            item.mainZoneMinimumCartAmount !== null &&
            item.mainZoneMinimumCartAmount !== undefined
              ? `${item.mainZoneMinimumCartAmount} ₺`
              : '-',
            item.storeZoneDeliveryFees?.length > 0
              ? item.storeZoneDeliveryFees
                  .map((zone) => {
                    const feeStrings = zone.deliveryFees?.map(
                      (fee) => `${fee.minCartAmount}-${fee.maxCartAmount}: ${fee.deliveryFee}`,
                    ) ?? [];
                    const chunks = [feeStrings.slice(0, 2).join(', ')];
                    for (let i = 2; i < feeStrings.length; i += 3) {
                      chunks.push(feeStrings.slice(i, i + 3).join(', '));
                    }
                    return `${zone.zoneName}: ${chunks.join('\n')}`;
                  })
                  .join('\n')
              : '-',
            item.ecomStatus === ECOM_STATUS.CLOSED ||
            item.qrStatus === null ||
            item.qrStatus === undefined
              ? '-'
              : item.qrStatus
                ? t('QR Okutma')
                : t('Buton'),
          ]),
        ];
        setDownloadedData(csvData);
        csvLink.current.link.click();
      }
    } catch (error) {
    } finally {
      setExportButtonLoading(false);
    }
  };

  const columns = [
    {
      dataIndex: 'code',
      title: t('storeCode'),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      dataIndex: 'name',
      title: t('storeName'),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      dataIndex: 'latitude',
      title: t('lat'),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      dataIndex: 'longitude',
      title: t('lon'),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('city'),
      dataIndex: 'city',
      sorter: true,
      showSorterTooltip: false,
      render: (city) => (city ? city.name : '-'),
    },
    {
      dataIndex: 'district',
      title: t('district'),
      sorter: true,
      showSorterTooltip: false,
      render: (district) => district?.name || '-',
    },
    {
      dataIndex: 'phoneNumber',
      title: t('phoneNumber'),
      sorter: true,
      showSorterTooltip: false,
      render: (phoneNumber) => (phoneNumber ? phoneNumber : '-'),
    },
    {
      dataIndex: 'storeType',
      title: t('storeType'),
      sorter: true,
      showSorterTooltip: false,
      render: (storeType) => (storeType ? storeType : '-'),
    },
    {
      title: t('storeStatus'),
      dataIndex: 'open',
      sorter: true,
      showSorterTooltip: false,
      render: (open) =>
        open ? (
          <Tag color={'green'}>{t('open')}</Tag>
        ) : (
          <Tag color={'red'}>{t('close')}</Tag>
        ),
    },
    {
      title: t('ccStatus'),
      dataIndex: 'clickAndCollect',
      sorter: true,
      showSorterTooltip: false,
      render: (clickAndCollect) =>
        clickAndCollect ? (
          <Tag color={'green'}>{t('yes')}</Tag>
        ) : (
          <Tag color={'red'}>{t('no')}</Tag>
        ),
    },
    {
      title: t('ecomStatus'),
      dataIndex: 'ecomStatus',
      sorter: true,
      showSorterTooltip: false,
      render: (ecomStatus) => (ecomStatus ? t(ecomStatus) : '-'),
    },
    {
      title: t('storeDailyStatus'),
      dataIndex: 'storeDailyStatuses',
      sorter: false,
      showSorterTooltip: false,
      render: (storeDailyStatuses) => {
        const today = new Date().setHours(0, 0, 0, 0);
        const todayStatus = storeDailyStatuses
          .filter(
            (entry) => new Date(entry.date).setHours(0, 0, 0, 0) === today,
          )
          .sort((a, b) => b.date - a.date)[0];
        return todayStatus ? (
          <Tag color={getColorByStatus(todayStatus.status)}>
            {t(todayStatus.status)}
          </Tag>
        ) : (
          '-'
        );
      },
    },
    {
      title: t('paymentMethod'),
      dataIndex: 'paymentTypeStores',
      sorter: false,
      showSorterTooltip: false,
      render: (paymentTypeStores) => {
        const maxWidth = 70;
        const text = paymentTypeStores
          ?.map((paymentType) => paymentType?.paymentType?.name)
          .join(', ');

        if (text && text.length > maxWidth) {
          return (
            <Tooltip title={text}>
              <div
                style={{
                  maxWidth: `${maxWidth}px`,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {text}
              </div>
            </Tooltip>
          );
        } else {
          return text || '-';
        }
      },
    },
    {
      title: t('suitableForPaymentAtDoor'),
      dataIndex: 'availablePaymentOnDelivery',
      sorter: true,
      showSorterTooltip: false,
      render: (availablePaymentOnDelivery) =>
        availablePaymentOnDelivery ? (
          <Tag color={availablePaymentOnDelivery ? 'green' : 'red'}>
            {availablePaymentOnDelivery ? t('yes') : t('no')}
          </Tag>
        ) : (
          '-'
        ),
    },
    {
      title: t('collectionType'),
      dataIndex: 'collectionType',
      sorter: true,
      showSorterTooltip: false,
      render: (collectionType) => t(collectionType),
    },
    {
      title: t('alternativeProduct'),
      dataIndex: 'alternativeProduct',
      sorter: true,
      showSorterTooltip: false,
      render: (alternativeProduct) => t(alternativeProduct),
    },
    {
      title: t('areaM2'),
      dataIndex: 'areaM2',
      sorter: true,
      showSorterTooltip: false,
      render: (areaM2) => (areaM2 !== null && areaM2 !== undefined ? areaM2 : '-'),
    },
    {
      title: t('courierStartEndTime'),
      dataIndex: 'qrStatus',
      sorter: true,
      showSorterTooltip: false,
      render: (qrStatus, record) =>
        record.ecomStatus === ECOM_STATUS.CLOSED ||
        qrStatus === null ||
        qrStatus === undefined
          ? '-'
          : qrStatus
            ? t('QR Okutma')
            : t('Buton'),
    },
    {
      width: 50,
      fixed: 'right',
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            menu={{
              items: [
                {
                  label: t('edit'),
                  icon: <EditOutlined />,
                  onClick: () => {
                    setIsEditModalOpen(true);
                    setSelectedStore(dataItem);
                  },
                },
                {
                  label: t('goToZones'),
                  icon: <ArrowRightOutlined />,
                  onClick: () => {
                    history.push(
                      `${PARENT_ROUTES.ZONE_MANAGEMENT}${PAGE_URLS.ZONES}`,
                    );
                    userRegions.forEach((userRegion) => {
                      if (userRegion?.id === dataItem?.region?.id) {
                        const storesOfRegion = userRegion.children || [];
                        setSelectedRegionAndStoreFromStores({
                          selectedRegion: [userRegion.id],
                          selectedStore: [dataItem.id],
                          storesOfRegion: storesOfRegion,
                        });
                      }
                    });
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
        ),
    },
  ];

  function showChangeStatusOfTemplate() {
    const ids = selectedRows.map((item) => item.code);
    const names = selectedRows.map((item) => item.name);
    const nameList = names.join(', ');
    confirm({
      title: t('modalDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: ` ${nameList} ${t('slotManagement.deleteTemplate')}`,
      okText: t('slotManagement.delete'),
      okType: 'primary',
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        deleteSlotTemplates({ ids: ids }).then((res) => {
          if (res) {
            notificationChangeStatus(STATUS.SUCCESS);
            lastQuery = null;
            const queryString = getQueryString();
            loadData(queryString);
          } else {
            notification.error({ message: res.message });
          }
        });
      },
      onCancel() {},
    });
  }

  const notificationChangeStatus = (type) => {
    notification[type]({
      message: t('slotManagement.templateEdited'),
      description: t('slotManagement.templateStatusChanged'),
    });
  };

  const getQueryString = () => {
    const isCentralOperationsExec = userRoles?.includes(
      AUTH_ROLE_CAN_APPROVE.CENTRAL_OPERATIONS_EXECUTIVE,
    );

    const baseFilter = isCentralOperationsExec
      ? ''
      : activeStoreID
        ? `filter=id==${activeStoreID}`
        : `filter=region.id==${activeRegionID}`;

    const filterString = Object.entries(filters)
      .filter(([item, value]) => value !== null && value !== undefined)
      .map(([item, value]) =>
        typeof value === 'boolean'
          ? `${item}==${encodeURI(value)}`
          : `${item}=${item === 'longitude' || item === 'latitude' ? '' : item === 'code' ? '' : 'like'}="${encodeURI(value)}"`,
      )
      .join(' and ');

    let queryString = isCentralOperationsExec
      ? `filter=${filterString}`
      : `${baseFilter} and ${filterString}`
          .trim()
          .replace(/\s*(?=(and))\1\s*$/, '');

    if (tableParams.column) {
      queryString += `&sort=${tableParams.column.dataIndex},${ORDER_TYPES[tableParams.order]}`;
    }

    if (initialRequest) {
      setInitialRequest(false);
    }

    queryString += `&page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;

    return queryString;
  };

  const getCSVExportQueryString = () => {
    const isCentralOperationsExec = userRoles?.includes(
      AUTH_ROLE_CAN_APPROVE.CENTRAL_OPERATIONS_EXECUTIVE,
    );

    const baseFilter = isCentralOperationsExec
      ? ''
      : activeStoreID
        ? `filter=id==${activeStoreID}`
        : `filter=region.id==${activeRegionID}`;

    const filterString = Object.entries(filters)
      .filter(([item, value]) => value !== null && value !== undefined)
      .map(([item, value]) =>
        typeof value === 'boolean'
          ? `${item}==${encodeURI(value)}`
          : `${item}=${item === 'longitude' || item === 'latitude' ? '' : 'like'}="${encodeURI(value)}"`,
      )
      .join(' and ');

    let queryString = isCentralOperationsExec
      ? `filter=${filterString}`
      : `${baseFilter} and ${filterString}`
          .trim()
          .replace(/\s*(?=(and))\1\s*$/, '');

    if (tableParams.column) {
      queryString += `&sort=${tableParams.column.dataIndex},${ORDER_TYPES[tableParams.order]}`;
    }

    if (initialRequest) {
      queryString += `${isCentralOperationsExec ? '' : '&'}deleted==false&sort=modifiedAt,desc`;
    }

    queryString += `&size=20000`;

    setInitialRequest(false);
    return queryString;
  };

  async function loadDataWithDebounce(queryString) {
    if (!loading) {
      lastQuery = queryString;
      setLoading(true);
      getStoreList(queryString)
        .then((res) => {
          const { content, page } = res;
          setRows(content ? content : []);
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page.totalElements,
            },
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs),
  );

  useEffect(() => {
    fetchData();
    setIsFiltersOpen(false);
    return () => {
      lastQuery = null;
      setLoading(false);
    };
  }, [JSON.stringify(tableParams), JSON.stringify(filters)]);

  const fetchData = () => {
    const queryString = getQueryString();
    loadData(queryString);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const actions = [
    {
      isCrudEnabled: true,
      id: 'downloadButton',
      type: '',
      title: t('download'),
      customContent: (
        <>
          <Button
            shape="round"
            onClick={fetchCSVData}
            icon={<DownloadOutlined />}
            loading={exportButtonLoading}
          />
          <CSVLink
            filename={t('Stores')}
            data={downloadedData}
            separator=";"
            bom={true}
            ref={csvLink}
            className="hidden"
          />
        </>
      ),
      disabled: false,
      hasDropdown: false,
    },
    {
      isCrudEnabled,
      type: '',
      title: t('import'),
      icon: <UploadOutlined />,
      onClick: () => setIsImportModalOpen(true),
      disabled: false,
    },
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      return (
        <Tooltip title={item.title} key={idx}>
          {item.isCrudEnabled &&
            (item.customContent ?? (
              <Button
                type={item?.type}
                shape="round"
                icon={item?.icon}
                onClick={item.onClick}
                disabled={item.disabled}
                style={{ marginBottom: '10px', marginRight: '3px' }}
                danger={item?.danger}
              />
            ))}
        </Tooltip>
      );
    });
  };
  const renderSelectedFilters = () => {
    const fieldLocalizationVars = {
      name: 'storeName',
      phoneNumber: 'phoneNumber',
      district: 'district',
      address: 'address',
      open: 'storeStatus',
      code: 'storeCode',
      latitude: 'lat',
      longitude: 'lon',
      faxNumber: 'faxNumber',
      secondPhoneNumber: 'phoneNumber2',
      storeType: 'storeType',
      clickAndCollect: 'ccStatus',
      'storeDailyStatuses.status': 'storeDailyStatus',
      alternativeProduct: 'alternativeProduct',
      ecomStatus: 'ecomStatus',
      collectionType: 'collectionType',
      'paymentTypeStores.paymentType.code': 'paymentType',
      'city.name': 'city',
      'user.nameSurname': 'regionalManager',
      'stores.id': 'storeId',
      availablePaymentOnDelivery: 'suitableForPaymentAtDoor',
    };
    return Object.keys(filters).map((item, idx) => {
      if (filters[item] !== null && filters[item] !== undefined) {
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {item === 'open'
              ? `${t('storeStatus')} : ${filters[item] ? t('open') : t('close')}`
              : item === 'clickAndCollect'
                ? `${t('ccStatus')} : ${filters[item] ? t('yes') : t('no')}`
                : item === 'storeDailyStatuses.status'
                  ? `${t(fieldLocalizationVars[item])} : ${t(filters[item])}`
                  : item === 'alternativeProduct'
                    ? `${t('alternativeProduct')} : ${filters[item] ? t('yes') : t('no')}`
                    : item === 'availablePaymentOnDelivery'
                      ? `${t('suitableForPaymentAtDoor')} : ${filters[item] ? t('yes') : t('no')}`
                      : item === 'ecomStatus'
                        ? `${t(fieldLocalizationVars[item])} : ${t(filters[item])}`
                        : item === 'collectionType'
                          ? `${t(fieldLocalizationVars[item])} : ${t(filters[item])}`
                          : item === 'paymentTypeStores.paymentType.code'
                            ? `${t(fieldLocalizationVars[item])} : ${t(filters[item])}`
                            : item === 'shipmentType'
                              ? `${t(fieldLocalizationVars[item])} : ${t(filters[item])}`
                              : item === 'collectionType'
                                ? `${t(fieldLocalizationVars[item])} : ${t(filters[item])}`
                                : `${t(fieldLocalizationVars[item])} : ${filters[item]}`}
          </Tag>
        );
      }
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
      }
    });
  };

  return (
    <>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{renderActionButtons()}</Col>
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) => record.code}
            scroll={{
              y: '70vh',
              x: '2000px',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<></>}>
        {isFiltersOpen && (
          <StoresTableFilters
            setIsFiltersOpen={setIsFiltersOpen}
            setFilters={setFilters}
            form={form}
          />
        )}
        ,
        {isEditModalOpen && (
          <Edit
            selectedStore={selectedStore}
            onCloseHandler={() => setIsEditModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
        {isImportModalOpen && (
          <ImportStoreInfo
            onCloseHandler={() => setIsImportModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
      </Suspense>
    </>
  );
};

export default StoresView;
